import { useTranslation } from "react-i18next";
import { PartialCompanyRegistration } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRejectCompany, postVerifyCompany, useGetPartialCompanyById } from "@/services/Company";
import { useState } from "react";
import { Grid } from "@mantine/core";

import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import Textarea from "@/components/atoms/Textarea";
import Drawer, { DrawerHeader } from "@/components/molecules/Drawer";
import SharedCompanyInfoTab from "../SharedTabs/SharedCompanyInfo";
import CTA from "@/components/atoms/CTA";

interface CompanyApplicationAdminDrawerProps {
  partialCompanyId: number;
  onClose: () => void;
  isOpen: boolean;
}

const CompanyApplicationAdminDrawer = ({ onClose, isOpen, partialCompanyId }: CompanyApplicationAdminDrawerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCloseHandler = () => {
    onClose();
    setMessage("");
  };

  const { data: partialCompany, isFetching: isFetchingPartialCompany } = useGetPartialCompanyById(partialCompanyId, {
    enabled: partialCompanyId > 0 && isOpen,
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: mutateApproveCompany, isPending: isPendingApproveCompany } = useMutation({
    mutationFn: async () => {
      if (partialCompany) return await postVerifyCompany(partialCompany?.partialCompanyId, message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompaniesToVerify"],
      });
      onClose();
    },
  });

  const { mutateAsync: mutateRejectCompany, isPending: isPendingRejectCompany } = useMutation({
    mutationFn: async () => {
      if (partialCompany) return await postRejectCompany(partialCompany?.partialCompanyId, message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompaniesToVerify"],
      });
      onCloseHandler();
      setIsModalOpen(false);
    },
  });

  const handleRejectCompany = async () => {
    setIsModalOpen(true);
  };

  // todo: spinner her
  if (!partialCompany || isFetchingPartialCompany) return null;

  return (
    <>
      <Drawer
        onClose={onCloseHandler}
        isOpen={isOpen}
        title={<DrawerHeader title={t("commonAdmin:newApplication")} svgName="document-timer" />}
        ctaRow={
          <Actions
            partialCompany={partialCompany}
            rejectFunction={handleRejectCompany}
            approveFunction={mutateApproveCompany}
            loadingApproveCompany={isPendingApproveCompany}
            loadingRejectCompany={isPendingRejectCompany}
            message={message}
            setMessage={setMessage}
            onClose={onCloseHandler}
          />
        }
      >
        <SharedCompanyInfoTab company={partialCompany} />
      </Drawer>

      <Modal opened={isModalOpen} onClose={() => setIsModalOpen(false)} title={t("commonAdmin:rejectApplication")}>
        <Text p={20}>{t("commonAdmin:confirmRejectCompanyApplication")}</Text>
        <ModalCTAs>
          <CTA intent="secondary" onClick={() => setIsModalOpen(false)}>
            {t("actions:cancel")}
          </CTA>
          <CTA onClick={() => mutateRejectCompany()}>{t("actions:accept")}</CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
};

export default CompanyApplicationAdminDrawer;

const ContactInfo = ({ partialCompany }: { partialCompany: PartialCompanyRegistration }) => {
  const { t } = useTranslation();
  const admin = partialCompany.partialCompany.admin;

  return (
    <Grid w="100%">
      <Grid.Col span={4}>
        <Text color="secondary" size="sm">
          {t("common:contact")}
        </Text>
      </Grid.Col>
      <Grid.Col span={8}>
        <Text size="sm">{`${admin.firstname} ${admin.lastname}`}</Text>
        <Text size="sm">{admin.email}</Text>
      </Grid.Col>
    </Grid>
  );
};

const Actions = ({
  partialCompany,
  rejectFunction,
  approveFunction,
  loadingApproveCompany,
  loadingRejectCompany,
  message,
  setMessage,
  onClose,
}: {
  partialCompany: PartialCompanyRegistration | null;
  rejectFunction?: () => void;
  approveFunction?: () => void;
  loadingApproveCompany?: boolean;
  loadingRejectCompany?: boolean;
  message: string;
  setMessage: (message: string) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Stack w="100%" gap="sm">
      {partialCompany && <ContactInfo partialCompany={partialCompany} />}

      {partialCompany?.status === "PENDING" ? (
        <>
          <Textarea label={t("common:comment")} value={message} onChange={(e) => setMessage(e.target.value)} />
          <Group justify="flex-end">
            <CTA intent="secondary" onClick={rejectFunction} loading={loadingRejectCompany || loadingApproveCompany}>
              {t("actions:reject")}
            </CTA>
            <CTA onClick={approveFunction} loading={loadingApproveCompany || loadingRejectCompany}>
              {t("actions:approve")}
            </CTA>
          </Group>
        </>
      ) : (
        <Group justify="flex-end">
          <CTA intent="secondary" onClick={onClose}>
            {t("actions:close")}
          </CTA>
        </Group>
      )}
    </Stack>
  );
};
